import { Option } from '@/models/option';
import { PackagePathSegment } from '@/models/package-path-segment';
import { PathSegment } from '@/models/path-segment';

export class PathUtil {
   /**
    * Checks if the current path of the log + newSegment is valid.
    * Every PID must equal the ID of the element before to meet that requirement.
    *
    * @param segmentLog The segment log to check
    * @param newSegment The new segment
    */
   public static isPathIntact(
      segmentLog: Array<PathSegment>,
      newSegment: PathSegment): boolean {
      if (segmentLog.length < 1)
         // if the log has no entries, we have a root entry - and as such the path is valid.
      {
         return true;
      }

      if (newSegment.dataObject?.pid !== segmentLog[segmentLog.length - 1].dataObject?.id)
         // if the new segment does not have the last log entry as a parent, we can already
         // tell that the chain has been broken
      {
         return false;
      }

      // if none of the above caught something, we have to walk every segment in the log
      // and check if every segment is positioned after their respective parent
      for (let i = segmentLog.length - 1; i > 1; i--) { // reverse loop, >1 because the next check would access a negative array index otherwise
         if (segmentLog[i].dataObject?.pid !== segmentLog[i - 1].dataObject?.id) {
            return false;
         }
      }

      // otherwise everything seems to be in order
      return true;
   }

   /**
    * Removes PathSegments from the log from back to front until the pid of the current segment
    * matches the ID of the segment in the log.
    *
    * @param segmentLog The Log of PathSegments to operate on
    * @param segment New Segment to check against
    */
   public static truncateLogUntilValid(
      segmentLog: Array<PathSegment>,
      segment: PathSegment): void {
      if (segmentLog.length < 1) {
         return;
      }

      if (segmentLog.length === 1) {
         const removedSegment = segmentLog.pop();
         if (removedSegment instanceof PackagePathSegment) {
            (removedSegment as PackagePathSegment).options.forEach(option => option.isPreSelected = false);
         }
         return;
      }

      // reverse loop, >1 because removing an element would cause a negative array index otherwise
      // for (let i = segmentLog.length - 1; i > 1; i--)
      // {
      //     // clear the log from back to front until the pid of the current segment
      //     // matches the ID of the segment in the log.
      //     if (segmentLog[i].dataObject?.id === segment.dataObject?.pid) {
      //         return;
      //     }
      //
      //     segmentLog.pop();
      // }

      while (!this.isPathIntact(segmentLog, segment)) {
         const removedSegment = segmentLog.pop();
         if (removedSegment instanceof PackagePathSegment) {
            (removedSegment as PackagePathSegment).options.forEach(option => option.isPreSelected = false);
         }
      }
   }

   /**
    * Returns the sum of the chosen segments from the specified segment log.
    * @param segmentLog
    * @param additionalOptions
    */
   public static calculatePriceForPath(
      segmentLog: Array<PathSegment>,
      additionalOptions: Array<Option>): number {
      if (!segmentLog || segmentLog.length < 1) {
         return 0.0;
      }

      const baseSum = segmentLog
         .map((segment: PathSegment) => {
            return segment instanceof PackagePathSegment
               ? segment.price || segment.options.reduce((
               pv,
               cv) => pv + cv.price, 0)
               : segment.price;
         })
         .reduce((
            sum: number,
            currentPrice: number) => sum + currentPrice);

      const additionalOptionsPrices = additionalOptions
         .filter((option: Option) => option.isSelected && !option.isPreSelected)
         .map((option: Option) => option.price);

      let additionalOptionsSum = 0.0;
      if (additionalOptionsPrices.length > 0) {
         additionalOptionsSum = additionalOptionsPrices
            .reduce((
               sum: number,
               currentPrice: number) => sum + currentPrice);
      }

      return baseSum + additionalOptionsSum;
   }

      /**
    * Returns the sum of the chosen segments from the specified segment log.
    * @param segmentLog
    * @param additionalOptions
    */
      public static calculateExternalPriceForPath(segmentLog: Array<PathSegment>, additionalOptions: Array<Option>): number
      {
         if (!segmentLog || segmentLog.length < 1) {
               return 0.0;
         }
   
         const baseSum = segmentLog
               .map((segment: PathSegment) => {
                  if(segment instanceof PackagePathSegment) {
                     if(segment.price) {
                           return segment.isCostExternal ? segment.price : 0;
                     }
                     return segment.options.reduce((pv, cv)=> {
                           return cv.isCostExternal ? pv + cv.price : pv;
                     }, 0);
                  }
                  return segment.isCostExternal ? segment.price : 0;
               })
               .reduce((sum: number, currentPrice: number) => sum + currentPrice);
   
         const additionalOptionsPrices = additionalOptions
               .filter((option: Option) => option.isSelected && !option.isPreSelected && option.isCostExternal)
               .map((option: Option) => option.price);
   
         let additionalOptionsSum = 0.0;
         if (additionalOptionsPrices.length > 0) {
               additionalOptionsSum = additionalOptionsPrices
                  .reduce((sum: number, currentPrice: number) => sum + currentPrice);
         }
   
         return baseSum + additionalOptionsSum;
      }

   public static isSegmentOnPath(
      path: Array<PathSegment>,
      segment: PathSegment): boolean {
      return path.includes(segment);
   }
}
